import { useEffect, useRef, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, Trash2, Upload } from "lucide-react";
import { API, api, errMsg, money, specValue, uploadFile, INCOTERMS } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import DashboardShell from "@/components/DashboardShell";
import { CompanyProfile } from "@/pages/BuyerPortal";
import SupplierProducts from "@/pages/SupplierProducts";
import CostSheet from "@/pages/CostSheet";
import SupplierCatalogueImport from "@/pages/SupplierCatalogueImport";
import SupplierSupport from "@/pages/SupplierSupport";
import SupplierTeam from "@/pages/SupplierTeam";
import { SupplierOrders } from "@/pages/Orders";
import Messages from "@/pages/Messages";
import SupplierInvoices from "@/pages/SupplierInvoices";
import { MandatoryTag, StatusChip, AvailabilityChip } from "@/components/StatusChip";
import { Amount, Fx } from "@/components/Currency";
import { CountrySelect } from "@/components/CountrySelect";

const TERM_OPTIONS = [
  ["advance_100", "100% advance payment"],
  ["advance_50_50", "50% advance / 50% on delivery"],
  ["advance_30_70", "30% advance / 70% on delivery"],
  ["advance_30_60_10", "30% advance / 60% shipment / 10% delivery"],
  ["on_delivery", "100% on delivery"],
  ["net_30", "Net 30 days from invoice"],
  ["net_45", "Net 45 days from invoice"],
  ["net_60", "Net 60 days from invoice"],
  ["lc", "Letter of Credit (LC) at sight"],
  ["custom", "Custom terms"],
];
import { Btn, Card, Empty, Field, Input, Loading, Pill, Select, StatCard, Table, Textarea, STATUS_TONE, labelCls } from "@/components/Ui";

const NAV = [
  { to: "/supplier", label: "Dashboard", end: true, testId: "supplier-nav-dashboard" },
  { to: "/supplier/rfqs", label: "Matching RFQs", testId: "supplier-nav-rfqs" },
  { to: "/supplier/quotes", label: "My Quotes", testId: "supplier-nav-quotes" },
  { to: "/supplier/opportunities", label: "Won / Lost", testId: "supplier-nav-opportunities" },
  { to: "/supplier/messages", label: "Messages", testId: "supplier-nav-messages" },
  { to: "/supplier/orders", label: "Orders", testId: "supplier-nav-orders" },
  { to: "/supplier/products", label: "My Products", testId: "supplier-nav-products" },
  { to: "/supplier/import-catalogue", label: "Import Catalogue", testId: "supplier-nav-import" },
  { to: "/supplier/documents", label: "Documents", testId: "supplier-nav-documents" },
  { to: "/supplier/subscription", label: "Subscription", testId: "supplier-nav-subscription" },
  { to: "/supplier/invoices", label: "Invoices", testId: "supplier-nav-invoices" },
  { to: "/supplier/team", label: "Team", testId: "supplier-nav-team" },
  { to: "/supplier/profile", label: "Company Profile", testId: "supplier-nav-profile" },
  { to: "/supplier/support", label: "Support", testId: "supplier-nav-support" },
];

function Overview() {
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  useEffect(() => {
    api.get("/supplier/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);
  const needsProfile = company && (company.categories || []).length === 0;
  return (
    <div data-testid="supplier-overview">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-matching" label="Matching RFQs" value={stats?.matching_rfqs} />
        <StatCard testId="stat-quotes" label="Quotes submitted" value={stats?.quotes_submitted} />
        <StatCard testId="stat-won" label="Won" value={stats?.won} />
        <StatCard testId="stat-winrate" label="Win rate"
          value={stats?.win_rate != null ? `${stats.win_rate}%` : "—"}
          sub={stats?.quotes_submitted ? `${stats.lost} lost` : "No quotes yet"} />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/supplier/rfqs"><Btn data-testid="supplier-open-rfqs-btn">View matching RFQs</Btn></Link>
        <Link to="/supplier/profile"><Btn variant="ghost" data-testid="supplier-profile-btn">Update capability profile</Btn></Link>
      </div>
      {needsProfile && (
        <Card className="mt-6 border-[#f0dcb4] bg-[#fff8ec]" data-testid="supplier-next-step">
          <h2 className="font-display text-base font-bold tracking-tight text-[#B7791F]">
            One step left before enquiries reach you
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-[#8a6423]">
            Tick the product categories you supply in your capability profile. Until you do, no RFQ
            can be matched to your company.
          </p>
          <Link to="/supplier/profile" className="mt-4 inline-block">
            <Btn data-testid="supplier-complete-profile-btn">Complete my profile</Btn>
          </Link>
        </Card>
      )}
      <Card className="mt-8">
        <h2 className="font-display text-base font-bold tracking-tight text-[#102A43]">How matching works</h2>
        <p className="mt-2 text-sm leading-relaxed text-[#667788]">
          You receive an enquiry only when it matches your registered categories, subcategories,
          brands or markets served. If an RFQ demands an exact brand you do not represent, it is not
          sent to you. Keep your capability profile accurate to receive the right opportunities.
        </p>
      </Card>
    </div>
  );
}

function MatchingRfqs() {
  const [data, setData] = useState(null);
  const [messaging, setMessaging] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/supplier/rfqs").then((r) => setData(r.data)).catch(() => setData({ results: [] }));
  }, []);

  const messageBuyer = async (rfqId) => {
    setMessaging(rfqId);
    try {
      await api.post("/messages/threads", { rfq_id: rfqId });
      toast.success("Conversation opened with the buyer");
      navigate("/supplier/messages");
    } catch (e) { toast.error(errMsg(e)); } finally { setMessaging(""); }
  };
  if (!data) return <Loading />;
  const { results = [], plan, visible_limit, total_matching } = data;
  return (
    <div data-testid="supplier-rfqs">
      <Card className="bg-[#F5F7FA]">
        <p className="text-sm text-[#334e68]">
          Your <strong>{(plan || "founding").toUpperCase()}</strong> plan shows up to{" "}
          <strong>{visible_limit}</strong> live matching enquiries. Currently matching:{" "}
          <strong>{total_matching}</strong>.
          {total_matching > (visible_limit || 0) && (
            <>
              {" "}
              <Link to="/supplier/subscription" className="font-semibold text-[#147D78] underline">
                Upgrade to see all matched RFQs
              </Link>
            </>
          )}
        </p>
      </Card>
      {results.length === 0 ? (
        <Empty testId="supplier-rfqs-empty" title="No matching enquiries right now"
          sub="Enquiries appear here when a buyer's requirement matches your registered categories, brands and markets."
          action={<Link to="/supplier/profile"><Btn>Review capability profile</Btn></Link>} />
      ) : (
        <div className="mt-4 space-y-4">
          {results.map((r) => (
            <Card key={r.id} data-testid={`supplier-rfq-card-${r.rfq_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-lg font-black tracking-tight text-[#102A43]">{r.rfq_number}</p>
                    {r.my_quote_id && (
                      <Pill tone={STATUS_TONE[r.my_quote_status] || "ok"} testId={`quoted-pill-${r.rfq_number}`}>
                        {r.my_quote_status === "revised" ? "Quote revised" : "Quote submitted"}
                      </Pill>
                    )}
                  </div>
                  <p className="text-sm text-[#334e68]">{r.title}</p>
                  <p className="mt-1 text-xs text-[#8598ac]">
                    {r.brand_requirement === "exact" ? `Exact brand required: ${r.requested_brand}` : "Equivalent accepted"}
                    {r.match_reasons?.length ? ` · ${r.match_reasons.join(", ")}` : ""}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Link to={`/supplier/rfqs/${r.id}/quote`}>
                    <Btn variant={r.my_quote_id ? "ghost" : "primary"} data-testid={`quote-rfq-btn-${r.rfq_number}`}
                      className="w-full px-4 py-2.5 text-xs">
                      {r.my_quote_id ? "Revise quotation" : "Submit quotation"}
                    </Btn>
                  </Link>
                  <Btn variant="ghost" className="px-4 py-2 text-xs"
                    data-testid={`message-buyer-btn-${r.rfq_number}`}
                    loading={messaging === r.id} onClick={() => messageBuyer(r.id)}>
                    Message buyer
                  </Btn>
                </div>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[#DFE6ED] pt-4 sm:grid-cols-4">
                {[["Quantity", `${r.quantity} ${r.unit}`],
                  ["Delivery location", r.delivery_location],
                  ["Required date", r.required_delivery_date || "Not specified"],
                  ["Closing date", r.closing_date || "—"],
                  ["Incoterm", r.incoterm || "—"],
                  ["Category", r.category || "—"],
                  ["Certificates", (r.required_certificates || []).join(", ") || "—"],
                  ["Submitted", (r.created_at || "").slice(0, 10)]].map(([l, v]) => (
                  <div key={l}>
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{l}</dt>
                    <dd className="text-sm font-medium text-[#18232F]">{v}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const initialQuote = {
  offered_manufacturer: "", offered_brand: "", part_number: "", quantity: "", unit_price: "",
  currency: "AED", country_of_origin: "", availability: "ex_stock", stock_country: "",
  stock_location: "", stock_quantity: "", lead_time_days: "", incoterm: "", freight: "",
  duty_pct: "", clearance: "", local_delivery: "", other_charges: "", payment_terms: "",
  payment_terms_options: [], payment_terms_custom: "",
  warranty_months: "", validity_days: "", technical_compliance: "meets", technical_deviations: "",
  certificate_references: "", notes: "",
};

const CONFIRMATION =
  "I confirm that the information submitted in this quotation is accurate and that our company is responsible for the offered product, specifications, documentation, delivery commitment, warranty and applicable seller obligations.";

function QuoteForm() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const schema = useSpecSchema();

  const [rfq, setRfq] = useState(null);
  // Requirement panel follows the RFQ's own category, not a fixed cable-gland list.
  const specDisplay = schema.displayFor(rfq?.category);

  const [f, setF] = useState(initialQuote);
  const [docs, setDocs] = useState([]);
  const [kind, setKind] = useState("commercial");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/supplier/rfqs/${rfqId}`).then(({ data }) => {
      setRfq(data);
      setF((p) => ({ ...p, quantity: data.quantity || "", incoterm: data.incoterm || "",
        offered_manufacturer: data.requested_brand || "", offered_brand: data.requested_brand || "",
        part_number: data.product?.part_number || "" }));
      if (data.my_quote_id) {
        // Revision: start from the previous quotation so nothing has to be retyped.
        api.get("/supplier/quotes").then(({ data: rows }) => {
          const q = rows.find((r) => r.id === data.my_quote_id);
          if (!q) return;
          setF((p) => {
            const next = { ...p };
            Object.keys(initialQuote).forEach((k) => {
              if (q[k] !== null && q[k] !== undefined) next[k] = q[k];
            });
            return next;
          });
          setDocs(q.documents || []);
        }).catch(() => {});
      }
    }).catch(() => setRfq(false));
  }, [rfqId]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const uploaded = await uploadFile(file, kind);
        setDocs((d) => [...d, uploaded]);
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err)}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    return send(false);
  };

  const send = async (draft) => {
    if (!draft && !accepted) return toast.error("You must accept the seller responsibility confirmation");
    if (!f.country_of_origin) return toast.error("Select the country of origin (manufactured in)");
    if (f.availability !== "to_order" && !f.stock_country)
      return toast.error("Select the country where the stock is currently held");
    if (f.availability === "partial" && Number(f.stock_quantity) > Number(f.quantity))
      return toast.error("Ex-stock quantity cannot exceed the quoted quantity");
    if (!f.payment_terms_options.length)
      return toast.error("Tick at least one payment term you accept");
    if (f.payment_terms_options.includes("custom") && !f.payment_terms_custom.trim())
      return toast.error("Describe your custom payment terms");
    setBusy(true);
    try {
      await api.post(`/supplier/rfqs/${rfqId}/quote${draft ? "?draft=true" : ""}`, {
        offered_manufacturer: f.offered_manufacturer, offered_brand: f.offered_brand || null,
        part_number: f.part_number, quantity: Number(f.quantity), unit_price: Number(f.unit_price),
        currency: f.currency, country_of_origin: f.country_of_origin || null,
        availability: f.availability,
        stock_country: f.availability === "to_order" ? null : (f.stock_country || null),
        stock_location: f.availability === "to_order" ? null : (f.stock_location || null),
        stock_quantity: f.availability === "partial" ? num(f.stock_quantity) : null,
        lead_time_days: num(f.lead_time_days), incoterm: f.incoterm || null,
        freight: Number(f.freight || 0), duty_pct: num(f.duty_pct),
        clearance: Number(f.clearance || 0), local_delivery: Number(f.local_delivery || 0),
        other_charges: Number(f.other_charges || 0),
        payment_terms_options: f.payment_terms_options,
        payment_terms_custom: f.payment_terms_custom || null,
        warranty_months: num(f.warranty_months), validity_days: num(f.validity_days),
        technical_compliance: f.technical_compliance,
        technical_deviations: f.technical_deviations || null,
        certificate_references: f.certificate_references || null, notes: f.notes || null,
        documents: docs.map((d) => ({ file_id: d.file_id, filename: d.filename, size: d.size,
          content_type: d.content_type, kind: d.kind || "other" })),
        confirmation_accepted: true,
      });
      toast.success(draft ? "Saved as a draft — not visible to the buyer yet" : "Quotation submitted");
      navigate("/supplier/quotes");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (rfq === null) return <Loading />;
  if (rfq === false) return <Empty title="This RFQ is not available to your account" />;

  const total = Number(f.unit_price || 0) * Number(f.quantity || 0);

  return (
    <div data-testid="quote-form-page">
      <Link to="/supplier/rfqs" className="text-xs font-semibold text-[#667788] hover:text-[#102A43]">
        ← Back to matching RFQs
      </Link>
      <div className="mt-3">
        <p className="font-display text-2xl font-black tracking-tight text-[#102A43]">
          Quote {rfq.rfq_number}
        </p>
        <p className="text-sm text-[#667788]">{rfq.title}</p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <form onSubmit={submit}>
          <Card>
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Offer</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Offered manufacturer *"><Input data-testid="q-manufacturer" required value={f.offered_manufacturer} onChange={set("offered_manufacturer")} /></Field>
              <Field label="Offered brand"><Input data-testid="q-brand" value={f.offered_brand} onChange={set("offered_brand")} /></Field>
              <Field label="Part number *"><Input data-testid="q-part-number" required value={f.part_number} onChange={set("part_number")} /></Field>
              <Field label="Country of origin (manufactured in) *"><CountrySelect testId="q-origin" required value={f.country_of_origin} onChange={(v) => setF({ ...f, country_of_origin: v })} /></Field>
              <Field label="Quantity *"><Input data-testid="q-quantity" required type="number" min="1" value={f.quantity} onChange={set("quantity")} /></Field>
              <Field label="Unit price *"><Input data-testid="q-unit-price" required type="number" step="0.01" min="0" value={f.unit_price} onChange={set("unit_price")} /></Field>
              <Field label="Currency"><Select data-testid="q-currency" value={f.currency} onChange={set("currency")} options={["AED", "USD", "EUR", "GBP"]} /></Field>
              <Field label="Incoterm"><Select data-testid="q-incoterm" value={f.incoterm} onChange={set("incoterm")} options={INCOTERMS} /></Field>
              <Field label="Lead time (days)"><Input data-testid="q-lead-time" type="number" min="0" value={f.lead_time_days} onChange={set("lead_time_days")} /></Field>
              <Field label="Quote validity (days)"><Input data-testid="q-validity" type="number" min="0" value={f.validity_days} onChange={set("validity_days")} /></Field>
              <Field label="Payment terms you accept *" className="sm:col-span-2"
                hint="Tick every option the buyer may choose from — they pick one when awarding">
                <div className="grid gap-2 sm:grid-cols-2" data-testid="q-payment-terms">
                  {TERM_OPTIONS.map(([k, l]) => (
                    <label key={k} data-testid={`q-term-${k}`}
                      className="flex cursor-pointer items-center gap-2.5 rounded-[9px] border border-[#DFE6ED] px-3 py-2.5 text-[13px]">
                      <input type="checkbox" className="h-4 w-4"
                        checked={f.payment_terms_options.includes(k)}
                        onChange={() => setF({
                          ...f,
                          payment_terms_options: f.payment_terms_options.includes(k)
                            ? f.payment_terms_options.filter((x) => x !== k)
                            : [...f.payment_terms_options, k],
                        })} />
                      <span className="text-[#334e68]">{l}</span>
                    </label>
                  ))}
                </div>
              </Field>
              {f.payment_terms_options.includes("custom") && (
                <Field label="Describe your custom terms *" className="sm:col-span-2">
                  <Input data-testid="q-payment-terms-custom" value={f.payment_terms_custom}
                    onChange={set("payment_terms_custom")}
                    placeholder="e.g. 40% with order, 60% against copy documents" />
                </Field>
              )}
              <Field label="Warranty (months)"><Input data-testid="q-warranty" type="number" min="0" value={f.warranty_months} onChange={set("warranty_months")} /></Field>
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Availability &amp; stock location
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Buyers see this on their comparison — state honestly where the goods physically are today.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Availability *">
                <Select data-testid="q-availability" value={f.availability}
                  onChange={(e) => setF({ ...f, availability: e.target.value })}
                  options={[["ex_stock", "Ex-stock (ready now)"],
                            ["partial", "Partially ex-stock"],
                            ["to_order", "Made to order"]]} />
              </Field>
              {f.availability !== "to_order" && (
                <>
                  <Field label="Stock currently held in (country) *">
                    <CountrySelect testId="q-stock-country" required value={f.stock_country}
                      onChange={(v) => setF({ ...f, stock_country: v })} />
                  </Field>
                  <Field label="City / free zone / warehouse">
                    <Input data-testid="q-stock-location" value={f.stock_location}
                      onChange={set("stock_location")} placeholder="e.g. Jebel Ali Free Zone" />
                  </Field>
                </>
              )}
              {f.availability === "partial" && (
                <Field label="Quantity available ex-stock *">
                  <Input data-testid="q-stock-quantity" required type="number" min="1"
                    value={f.stock_quantity} onChange={set("stock_quantity")} />
                </Field>
              )}
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Delivered cost components
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Used to build the buyer&apos;s estimated landed cost. Leave blank if included in your price.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-5">
              {[["freight", "Freight"], ["duty_pct", "Duty %"], ["clearance", "Clearance"],
                ["local_delivery", "Local delivery"], ["other_charges", "Other charges"]].map(([k, l]) => (
                <Field key={k} label={l}>
                  <Input data-testid={`q-${k}`} type="number" step="0.01" value={f[k]} onChange={set(k)} />
                </Field>
              ))}
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
              Technical compliance
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Compliance against the requested specification *">
                <Select data-testid="q-compliance" value={f.technical_compliance} onChange={set("technical_compliance")}
                  options={[["meets", "Fully meets the requirement"], ["deviation", "Technical deviation declared"]]} />
              </Field>
              <Field label="Certificate references">
                <Input data-testid="q-certificates" value={f.certificate_references} onChange={set("certificate_references")}
                  placeholder="IECEx BAS 06.0031X / ATEX …" />
              </Field>
              <Field label="Declared technical deviations" className="sm:col-span-2">
                <Textarea data-testid="q-deviations" rows={3} value={f.technical_deviations} onChange={set("technical_deviations")}
                  placeholder="State any parameter that differs from the requested specification" />
              </Field>
              <Field label="Notes to the buyer" className="sm:col-span-2">
                <Textarea data-testid="q-notes" rows={3} value={f.notes} onChange={set("notes")} />
              </Field>
            </div>
          </Card>

          <Card className="mt-5">
            <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">Documents</h2>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <Field label="Document type">
                <Select data-testid="q-doc-kind" value={kind} onChange={(e) => setKind(e.target.value)}
                  options={[["commercial", "Commercial quotation"], ["datasheet", "Datasheet"],
                    ["specification", "Certificate"], ["other", "Other"]]} />
              </Field>
              <input ref={fileRef} data-testid="q-file-input" type="file" multiple onChange={onFiles} className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.dwg,.dxf,.zip" />
              <Btn type="button" variant="ghost" loading={uploading} data-testid="q-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="border-dashed border-[#147D78] bg-[#f2f8f7] text-[#147D78]">
                <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Attach quotation, datasheet, certificates"}
              </Btn>
            </div>
            {docs.length > 0 && (
              <ul className="mt-3 space-y-2" data-testid="q-doc-list">
                {docs.map((d) => (
                  <li key={d.file_id} className="flex items-center justify-between rounded-[9px] border border-[#DFE6ED] px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <Paperclip className="h-3.5 w-3.5 shrink-0 text-[#667788]" />
                      <span className="truncate">{d.filename}</span>
                    </span>
                    <button type="button" onClick={() => setDocs((x) => x.filter((y) => y.file_id !== d.file_id))}
                      className="text-[#B42318]" data-testid={`q-remove-doc-${d.file_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="mt-5">
            <label className="flex cursor-pointer gap-3 text-sm leading-relaxed text-[#334e68]">
              <input data-testid="q-confirmation" type="checkbox" checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#147D78]" />
              <span>{CONFIRMATION}</span>
            </label>
            <Btn type="submit" data-testid="q-submit-btn" loading={busy} disabled={!accepted} className="mt-5 w-full sm:w-auto">
              Submit quotation
            </Btn>
            <Btn type="button" variant="ghost" data-testid="q-draft-btn" loading={busy}
              onClick={() => send(true)} className="mt-3 w-full sm:ml-3 sm:mt-5 sm:w-auto">
              Save as draft
            </Btn>
          </Card>
        </form>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card data-testid="rfq-requirement-panel">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
              Buyer requirement
            </p>
            <p className="mt-2 text-sm font-semibold text-[#102A43]">
              {rfq.quantity} {rfq.unit} · {rfq.delivery_location}
            </p>
            <p className="text-xs text-[#8598ac]">
              {rfq.brand_requirement === "exact" ? `Exact brand: ${rfq.requested_brand}` : "Equivalent accepted"}
            </p>
            <dl className="mt-4 space-y-2.5 border-t border-[#DFE6ED] pt-4">
              {specDisplay.map(([key, l, field]) => (
                <div key={key} className="flex justify-between gap-4 text-xs">
                  <dt className="flex items-center text-[#667788]">
                    {l}
                    <MandatoryTag mandatory={(rfq.mandatory_parameters || []).includes(key)} />
                  </dt>
                  <dd className="text-right font-semibold text-[#18232F]">{schema.specValue(rfq.spec, field)}</dd>
                </div>
              ))}
            </dl>
            {rfq.acceptable_products?.length > 0 && (
              <div className="mt-4 rounded-[9px] border border-[#DFE6ED] bg-[#F8FAFC] p-3"
                data-testid="rfq-acceptable-products">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
                  Buyer accepts any of these parts
                </p>
                <ul className="mt-2 space-y-1 text-xs text-[#334e68]">
                  {rfq.product_summary && <li className="font-semibold text-[#102A43]">{rfq.product_summary}</li>}
                  {rfq.acceptable_products.map((a) => (
                    <li key={a.product_id}>
                      <span className="font-semibold text-[#102A43]">{a.part_number}</span> — {a.manufacturer_name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {rfq.notes && <p className="mt-4 rounded-[9px] bg-[#F5F7FA] p-3 text-xs text-[#334e68]">{rfq.notes}</p>}            {rfq.attachments?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {rfq.attachments.map((a) => (
                  <a key={a.file_id} href={`${API}/files/${a.file_id}`} target="_blank" rel="noreferrer"
                    className="rounded-full border border-[#DFE6ED] px-3 py-1.5 text-xs font-semibold text-[#102A43]">
                    {a.filename}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-5 border-t border-[#DFE6ED] pt-4">
              <p className={labelCls}>Your offer total</p>
              <p className="font-display text-xl font-black text-[#102A43]">{money(total, f.currency)}</p>
              <Fx amount={total} from={f.currency} className="text-xs font-semibold" />
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function MyQuotes() {
  const [quotes, setQuotes] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/supplier/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await api.post(`/supplier/quotes/${id}/status`, { action });
      toast.success(action === "send" ? "Quotation sent to the buyer"
        : action === "approve" ? "Quotation approved and ready to send"
        : action === "withdraw" ? "Quotation withdrawn" : "Quotation reopened as a draft");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const downloadPdf = async (q) => {
    try {
      const { data } = await api.get(`/supplier/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quote_number || "quotation"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!quotes) return <Loading />;
  if (!quotes.length) return <Empty testId="supplier-quotes-empty" title="No quotations submitted yet"
    sub="Open a matching RFQ and submit a structured quotation." />;
  const stageLabel = { draft: "Draft", approved: "Approved — not sent", submitted: "Sent",
    revised: "Sent (revised)", withdrawn: "Withdrawn", won: "Won", lost: "Lost" };
  return (
    <div data-testid="supplier-quotes">
      <Table head={["Quotation", "RFQ", "Part", "Unit price", "Total", "Availability", "Rev.", "Status", "Actions"]}>
        {quotes.map((q) => (
          <tr key={q.id} data-testid={`my-quote-row-${q.id}`}>
            <td className="font-semibold text-[#102A43]" data-testid={`quote-number-${q.id}`}>
              {q.quote_number || "—"}
            </td>
            <td>{q.rfq_number}</td>
            <td>{q.offered_manufacturer} {q.part_number}</td>
            <td><Amount value={q.unit_price} currency={q.currency} /></td>
            <td><Amount value={q.total_price} currency={q.currency} block /></td>
            <td><AvailabilityChip quote={q} /></td>
            <td>{q.revision}</td>
            <td><Pill tone={STATUS_TONE[q.status] || "neutral"} testId={`my-quote-status-${q.id}`}>
              {stageLabel[q.status] || q.status}
            </Pill></td>
            <td>
              <div className="flex flex-wrap gap-2">
                {q.status === "draft" && (
                  <Btn variant="ghost" className="px-3 py-1.5 text-xs" loading={busy === q.id + "approve"}
                    data-testid={`approve-quote-${q.id}`} onClick={() => act(q.id, "approve")}>
                    Approve
                  </Btn>
                )}
                {["draft", "approved"].includes(q.status) && (
                  <Btn variant="primary" className="px-3 py-1.5 text-xs" loading={busy === q.id + "send"}
                    data-testid={`send-quote-${q.id}`} onClick={() => act(q.id, "send")}>
                    Send to buyer
                  </Btn>
                )}
                {["submitted", "revised", "approved"].includes(q.status) && (
                  <Btn variant="danger" className="px-3 py-1.5 text-xs" loading={busy === q.id + "withdraw"}
                    data-testid={`withdraw-quote-${q.id}`} onClick={() => act(q.id, "withdraw")}>
                    Withdraw
                  </Btn>
                )}
                {q.status === "withdrawn" && (
                  <Btn variant="ghost" className="px-3 py-1.5 text-xs" loading={busy === q.id + "reopen"}
                    data-testid={`reopen-quote-${q.id}`} onClick={() => act(q.id, "reopen")}>
                    Reopen as draft
                  </Btn>
                )}
                <Link to={`/supplier/quotes/${q.id}/cost-sheet`}
                  className="text-xs font-bold text-[#147D78] underline"
                  data-testid={`cost-sheet-link-${q.id}`}>
                  Cost sheet
                </Link>
                <button type="button" onClick={() => downloadPdf(q)}
                  className="text-xs font-bold text-[#102A43] underline"
                  data-testid={`quote-pdf-${q.id}`}>
                  Print / PDF
                </button>
                {q.rfq_status === "open" && (
                  <Link to={`/supplier/rfqs/${q.rfq_id}/quote`}
                    className="text-xs font-bold text-[#667788] underline">
                    Revise
                  </Link>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Opportunities() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/supplier/opportunities").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <Loading />;
  const groups = [["won", "Won"], ["lost", "Lost"], ["pending", "Awaiting decision"]];
  return (
    <div data-testid="supplier-opportunities" className="space-y-8">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Quotes" value={data.summary.quotes} />
        <StatCard label="Won" value={data.summary.won} />
        <StatCard label="Lost" value={data.summary.lost} />
        <StatCard label="Win rate" value={data.summary.win_rate != null ? `${data.summary.win_rate}%` : "—"} />
      </div>
      {groups.map(([k, title]) => (
        <div key={k}>
          <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">{title}</h2>
          {data[k].length === 0 ? (
            <Empty testId={`opps-empty-${k}`} title={`Nothing ${title.toLowerCase()} yet`} />
          ) : (
            <Table head={["RFQ", "Title", "Part", "Value"]}>
              {data[k].map((i) => (
                <tr key={i.quote_id} data-testid={`opp-row-${i.quote_id}`}>
                  <td className="font-semibold text-[#102A43]">{i.rfq_number}</td>
                  <td>{i.title}</td>
                  <td>{i.part_number}</td>
                  <td><Amount value={i.total_price} currency={i.currency} block /></td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      ))}
    </div>
  );
}

function Documents() {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/supplier/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])); }, []);
  if (!docs) return <Loading />;
  if (!docs.length) return <Empty testId="supplier-docs-empty" title="No documents yet"
    sub="Documents you attach to quotations and your company profile appear here." />;
  return (
    <div data-testid="supplier-documents">
      <Table head={["File", "Type", "Source", ""]}>
        {docs.map((d, i) => (
          <tr key={`${d.file_id}-${i}`}>
            <td className="font-semibold text-[#102A43]">{d.filename}</td>
            <td>{d.kind || "other"}</td>
            <td>{d.source || "Company profile"}</td>
            <td><a href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer"
              className="text-xs font-bold text-[#147D78] underline">Open</a></td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function Subscription() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/supplier/subscription").then((r) => setData(r.data)).catch(() => setData(null));
  // Mount-only fetch; `api` is a module singleton and `load` is defined above with stable setters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const request = async (plan) => {
    setBusy(plan);
    try {
      await api.post("/supplier/subscription/request", { plan });
      toast.success("Downgrade request sent to the ADIT team");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  return (
    <div data-testid="supplier-subscription">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">Current plan</p>
            <p className="mt-1 font-display text-2xl font-black text-[#102A43]">{data.plan_detail?.name}</p>
          </div>
          <div className="flex gap-2">
            <Pill tone={STATUS_TONE[data.status] || "neutral"} testId="subscription-status">{data.status}</Pill>
            {data.requested_plan && <Pill tone="warn" testId="requested-plan">{data.requested_plan} requested</Pill>}
          </div>
        </div>
        <p className="mt-4 text-sm text-[#667788]">{data.note}</p>
      </Card>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {["founding", "pro", "enterprise"].map((key) => {
          const p = data.all_plans[key];
          const current = data.plan === key;
          return (
            <Card key={key} data-testid={`sub-plan-${key}`} className={current ? "border-[#102A43] ring-1 ring-[#102A43]" : ""}>
              <h3 className="font-display text-lg font-black tracking-tight text-[#102A43]">{p.name}</h3>
              <p className="mt-3 font-display text-2xl font-black text-[#102A43]">
                {p.price_aed ? `AED ${p.price_aed.toLocaleString()}` : "Free"}
              </p>
              <p className="text-xs text-[#667788]">{p.billing}</p>
              <ul className="mt-4 space-y-2 border-t border-[#DFE6ED] pt-4 text-sm text-[#334e68]">
                {p.features.map((f) => <li key={f}>• {f}</li>)}
              </ul>
              {current ? (
                <p className="mt-5 text-center text-xs font-bold uppercase tracking-[0.12em] text-[#147D78]">
                  Current plan
                </p>
              ) : key === "founding" ? (
                <Btn variant="ghost" data-testid={`request-plan-${key}`} loading={busy === key}
                  onClick={() => request(key)} className="mt-5 w-full">
                  Request downgrade
                </Btn>
              ) : (
                <Btn data-testid={`checkout-plan-${key}`} loading={busy === key}
                  onClick={() => checkout(key)} className="mt-5 w-full">
                  Subscribe — AED {p.price_aed.toLocaleString()}/year
                </Btn>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function SupplierPortal() {
  return (
    <DashboardShell title="Supplier dashboard" subtitle="Opportunity workspace" nav={NAV} testId="supplier-dashboard">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="rfqs" element={<MatchingRfqs />} />
        <Route path="rfqs/:rfqId/quote" element={<QuoteForm />} />
        <Route path="quotes" element={<MyQuotes />} />
        <Route path="quotes/:quoteId/cost-sheet" element={<CostSheet />} />
        <Route path="opportunities" element={<Opportunities />} />
        <Route path="products" element={<SupplierProducts />} />
        <Route path="orders" element={<SupplierOrders />} />
        <Route path="messages" element={<Messages role="supplier" />} />
        <Route path="documents" element={<Documents />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="invoices" element={<SupplierInvoices />} />
        <Route path="team" element={<SupplierTeam />} />
        <Route path="profile" element={<CompanyProfile supplier />} />
        <Route path="import-catalogue" element={<SupplierCatalogueImport />} />
        <Route path="support" element={<SupplierSupport />} />      </Routes>
    </DashboardShell>
  );
}
